import type { StoredCalendarEvent } from '@packages/types';
import { Badge } from '../ui/Badge';
import { formatEventTimeRange } from '../../routes/calendar.helpers';

interface CalendarEventPreviewProps {
  event: StoredCalendarEvent;
}

/**
 * Inline preview card for a calendar event linked to an activity. Mirrors
 * EmailPreview's visual language: small bordered card with a title row, a
 * key-value list, and (if present) a description body. Shown in the activity
 * diary and the activity modal so the user can see what calendar event the
 * draft was logged from.
 */
export function CalendarEventPreview({ event }: CalendarEventPreviewProps) {
  const { start, duration } = formatEventTimeRange(event);
  const date = new Date(event.startAt).toLocaleDateString('en-ZA', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
  const externalAttendees = event.attendees.filter((a) => !a.isSelf);

  return (
    <div className="rounded-lg border border-border bg-bg-subtle p-4">
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <Badge variant="info">Calendar event</Badge>
        {event.cancelledAt && <Badge variant="error">Cancelled</Badge>}
        {event.recurringEventId && <Badge>Recurring</Badge>}
        <h4 className="text-sm font-semibold text-secondary">
          {event.subject || '(No subject)'}
        </h4>
      </div>

      <div className="space-y-1 text-sm text-text-muted">
        <div>
          <span className="font-medium">When:</span>{' '}
          {event.isAllDay ? `${date} - All day` : `${date}, ${start} (${duration})`}
        </div>
        {event.organiser && (
          <div>
            <span className="font-medium">Organiser:</span>{' '}
            {event.organiser.displayName
              ? `${event.organiser.displayName} <${event.organiser.email}>`
              : event.organiser.email}
          </div>
        )}
        {externalAttendees.length > 0 && (
          <div>
            <span className="font-medium">Attendees:</span>{' '}
            {externalAttendees
              .map((a) => (a.displayName ? `${a.displayName} <${a.email}>` : a.email))
              .join(', ')}
          </div>
        )}
        {event.location && (
          <div>
            <span className="font-medium">Location:</span> {event.location}
          </div>
        )}
      </div>

      {event.description && (
        <p className="mt-3 whitespace-pre-wrap border-t border-border pt-3 text-sm text-secondary">
          {event.description}
        </p>
      )}
    </div>
  );
}
